import { Fragment } from 'react'
import { flatCols } from '../../../hooks/useMinOneDayData'

function groupTotalCols(group) {
  if (group.subgroups) return group.subgroups.reduce((s, sg) => s + sg.cols.length, 0)
  return group.cols.length
}

function allSameUnit(cols) {
  return cols.length > 0 && cols.every(c => c.unit === cols[0].unit)
}

function buildColBorders(columns) {
  const borders = []
  columns.forEach((g, gi) => {
    const cols = g.subgroups ? g.subgroups.flatMap(sg => sg.cols) : g.cols
    const subgroupStarts = new Set()
    if (g.subgroups) {
      let idx = 0
      g.subgroups.forEach((sg, sgi) => {
        if (sgi > 0) subgroupStarts.add(idx)
        idx += sg.cols.length
      })
    }
    cols.forEach((_, ci) => {
      if (ci === 0 && gi > 0) borders.push('border-l border-l-gray-400')
      else if (subgroupStarts.has(ci)) borders.push('border-l border-l-gray-300')
      else borders.push('')
    })
  })
  return borders
}

function formatValue(value) {
  if (typeof value === 'number') return value % 1 === 0 ? String(value) : value.toFixed(1)
  return String(value)
}

function formatDateStr(dateStr) {
  const [y, m, d] = dateStr.split('-')
  return `${d}.${m}.${y}`
}

const cellBorder = 'border-b border-r border-gray-200 last:border-r-0'
const thBase = `${cellBorder} px-2 py-1 text-xs font-normal text-center bg-gray-50 whitespace-nowrap`
const th1 = `${thBase} font-semibold text-gray-600 border-t-2 border-t-gray-400`
const th2 = `${thBase} font-medium text-gray-500`
const th3 = `${thBase} text-gray-400`

export function MinGrid({ columns, headerTransposed, orientation = 'normal', dataMap, labelMap, dayMap, rowGroupBorder, rowGroupEndBorder, rowLabel = 'Čas', reverse = false }) {
  if (orientation === 'transposed' && headerTransposed) {
    return (
      <MinGridTransposed
        header={headerTransposed}
        dataMap={dataMap}
        labelMap={labelMap}
        dayMap={dayMap}
        colGroupStartBorder={rowGroupBorder}
        colLabel={rowLabel}
      />
    )
  }

  const allColumns = flatCols(columns)
  const colBorders = buildColBorders(columns)
  const times = Array.from((labelMap ?? dataMap).keys()).sort()
  if (reverse) times.reverse()
  const thEndBorder = rowGroupEndBorder ? 'border-b-gray-500' : ''

  return (
    <div className="h-full overflow-hidden border border-gray-200">
    <div className="h-full overflow-auto">
      <table className="border-separate border-spacing-0 border-l border-l-gray-400 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
        <thead className="sticky top-0 z-20">
          {/* Row 1 – group names */}
          <tr>
            <th rowSpan={3} className={`sticky left-0 z-10 bg-gray-50 border-t-2 border-t-gray-400 border-b border-b-gray-200 border-r border-r-gray-400 px-2 py-1 text-gray-500 font-normal whitespace-nowrap ${thEndBorder}`}>
              {rowLabel}
            </th>
            {columns.map((g, gi) => (
              <th
                key={g.group}
                colSpan={groupTotalCols(g)}
                className={`${th1} ${gi > 0 ? 'border-l border-l-gray-400' : ''}`}
              >
                {g.group}
              </th>
            ))}
          </tr>

          {/* Row 2 – subgroup labels / element labels / empty for single-col groups */}
          <tr>
            {columns.flatMap((g, gi) => {
              const firstBorder = gi > 0 ? 'border-l border-l-gray-400' : ''
              if (g.subgroups) {
                return g.subgroups.map((sg, sgi) => (
                  <th
                    key={`${g.group}-${sg.label}`}
                    colSpan={sg.cols.length}
                    className={`${th2} ${sgi === 0 ? firstBorder : 'border-l border-l-gray-300'}`}
                  >
                    {sg.label}
                  </th>
                ))
              }
              if (g.cols.length === 1) {
                return [<th key={g.group} className={`${th2} ${firstBorder}`} />]
              }
              return g.cols.map((col, ci) => (
                <th key={col.tsId} className={`${th2} ${ci === 0 ? firstBorder : ''}`}>
                  {col.label}
                </th>
              ))
            })}
          </tr>

          {/* Row 3 – units (merge identical units within group/subgroup) */}
          <tr>
            {columns.flatMap((g, gi) => {
              const firstBorder = gi > 0 ? 'border-l border-l-gray-400' : ''
              if (g.subgroups) {
                return g.subgroups.flatMap((sg, sgi) => {
                  const border = sgi === 0 ? firstBorder : 'border-l border-l-gray-300'
                  if (allSameUnit(sg.cols)) {
                    return [<th key={`${g.group}-${sg.label}-u`} colSpan={sg.cols.length} className={`${th3} ${border} ${thEndBorder}`}>{sg.cols[0].unit}</th>]
                  }
                  return sg.cols.map((col, ci) => (
                    <th key={`${col.tsId}-u`} className={`${th3} ${ci === 0 ? border : ''} ${thEndBorder}`}>{col.unit}</th>
                  ))
                })
              }
              const cols = g.cols
              if (allSameUnit(cols)) {
                return [<th key={`${g.group}-u`} colSpan={cols.length} className={`${th3} ${firstBorder} ${thEndBorder}`}>{cols[0].unit}</th>]
              }
              return cols.map((col, ci) => (
                <th key={`${col.tsId}-u`} className={`${th3} ${ci === 0 ? firstBorder : ''} ${thEndBorder}`}>{col.unit}</th>
              ))
            })}
          </tr>
        </thead>

        <tbody>
          {times.map((time, idx) => {
            const entry = dataMap.get(time)
            const label = labelMap?.get(time) ?? time
            const info  = dayMap?.get(time)
            const prevTime = idx > 0 ? times[idx - 1] : null
            const dayBorder = !!prevTime && dayMap?.get(time)?.dateStr !== dayMap?.get(prevTime)?.dateStr
            const groupBorder    = dayBorder ? 'border-t-2 border-t-gray-400' : rowGroupBorder?.(time, label) ? 'border-t border-t-gray-400' : ''
            const groupEndBorder = rowGroupEndBorder?.(time) ? 'border-b-gray-500' : ''
            return (
              <tr key={time} className="group">
                <td className={`sticky left-0 z-10 bg-gray-50 border-b border-b-gray-200 border-r border-r-gray-400 group-last:border-b-0 px-2 py-0.5 text-gray-500 text-right whitespace-nowrap font-mono ${groupBorder} ${groupEndBorder}`}>
                  {dayBorder && info?.dateStr ? (
                    <span className="flex flex-col items-end gap-0">
                      <span className="text-gray-400 text-xs">{formatDateStr(info.dateStr)}</span>
                      <span>{label}</span>
                    </span>
                  ) : label}
                </td>
                {(() => {
                  const cells = []
                  let skip = 0
                  allColumns.forEach((col, ci) => {
                    if (skip > 0) { skip--; return }
                    const value = entry?.[col.tsId] ?? null
                    const title = value != null && col.titleCell ? col.titleCell(value, time) : undefined
                    const span = col.colSpan ? col.colSpan(entry ?? {}) : 1
                    if (span > 1) skip = span - 1
                    cells.push(
                      <td
                        key={col.tsId}
                        title={title}
                        colSpan={span > 1 ? span : undefined}
                        className={`${cellBorder} group-last:border-b-0 px-1 py-0.5 text-right tabular-nums whitespace-nowrap bg-white ${colBorders[ci]} ${value == null ? 'text-gray-300' : ''} ${groupBorder} ${groupEndBorder}`}
                      >
                        {value != null ? (col.formatCell ? col.formatCell(value, time) : formatValue(value)) : '—'}
                      </td>
                    )
                  })
                  return cells
                })()}
              </tr>
            )
          })}
          {times.length === 0 && (
            <tr>
              <td colSpan={allColumns.length + 1} className="px-4 py-6 text-center text-gray-400 border-b border-r border-gray-200 last:border-r-0 border-b-0">
                Žádná data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </div>
  )
}

// ── Transponovaná varianta ─────────────────────────────────────────────────────
// řádky = veličiny (header.rows), sloupce = časové kroky (labelMap).
// Vzhled sjednocený s YlyStatsGrid (netransponovaný): 1 sticky sloupec "Veličina" (min-w 16rem,
// popis + jednotka "(°C)"), skupina veličiny = řádek hlavičky přes celou šířku (bg-gray-100,
// uppercase). Tenké čáry gray-200, gray-400 jen kolem hlavičky a sticky sloupce.
//
// header: {
//   groupLabel?: string           // popisek 1. sloupce (default "Veličina")
//   rows: [{ tsId, group?, label, unit, formatCell?, titleCell? }]
// }
const tCb = 'border-b border-r border-gray-200 px-1 py-0.5 text-xs tabular-nums whitespace-nowrap'
const tThBase = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1 py-1 text-xs font-normal text-center bg-gray-50'

function MinGridTransposed({ header, dataMap, labelMap, dayMap, colGroupStartBorder, colLabel }) {
  const flatRows = header.rows ?? []
  const times = Array.from((labelMap ?? dataMap).keys()).sort()
  const groupLabel = header.groupLabel ?? 'Veličina'

  // group veličin -> pole řádků; zachovává pořadí
  const groups = []
  for (const row of flatRows) {
    const key = row.group ?? ''
    let g = groups.find(x => x.label === key)
    if (!g) { g = { label: key, rows: [] }; groups.push(g) }
    g.rows.push(row)
  }

  // Svislá oddělovací čára na začátku časové skupiny (celé hodiny apod.) – stejně jako
  // GrafDataGrid transponovaně: silnější (2px) na hranici dne, tenká na hranici skupiny.
  const colStart = (time, idx) => {
    const label = labelMap?.get(time) ?? time
    const prevTime = idx > 0 ? times[idx - 1] : null
    const dayBorder = !!prevTime && dayMap?.get(time)?.dateStr !== dayMap?.get(prevTime)?.dateStr
    if (dayBorder) return 'border-l-2 border-l-gray-400'
    if (idx > 0 && colGroupStartBorder?.(time, label)) return 'border-l border-l-gray-400'
    return ''
  }

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[16rem] whitespace-nowrap"
              >
                {groupLabel}
              </th>
              {times.map((time, idx) => {
                const label = labelMap?.get(time) ?? time
                const info = dayMap?.get(time)
                const prevTime = idx > 0 ? times[idx - 1] : null
                const dayBorder = !!prevTime && dayMap?.get(time)?.dateStr !== dayMap?.get(prevTime)?.dateStr
                return (
                  <th
                    key={time}
                    title={colLabel}
                    className={`${tThBase} border-t-2 border-t-gray-400 border-b-gray-400 text-gray-500 ${colStart(time, idx)}`}
                  >
                    {dayBorder && info?.dateStr ? (
                      <span className="flex flex-col items-center gap-0">
                        <span className="text-gray-400 text-[10px]">{formatDateStr(info.dateStr)}</span>
                        <span>{label}</span>
                      </span>
                    ) : label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <Fragment key={g.label || `g${gi}`}>
                {g.label && (
                  <tr>
                    <td className="sticky left-0 z-10 bg-gray-100 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {g.label}
                    </td>
                    {times.map((time, idx) => (
                      <td key={time} className={`${tCb} bg-gray-100 ${colStart(time, idx)}`} />
                    ))}
                  </tr>
                )}
                {g.rows.map((row) => (
                  <tr key={row.tsId} className="group hover:bg-slate-50">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap">
                      {row.label}
                      {row.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({row.unit})</span>}
                    </td>
                    {times.map((time, idx) => {
                      const entry = dataMap.get(time) ?? {}
                      const value = entry[row.tsId] ?? null
                      const title = value != null && row.titleCell ? row.titleCell(value, time) : undefined
                      return (
                        <td
                          key={time}
                          title={title}
                          className={`${tCb} text-right bg-white group-hover:bg-slate-50 ${value == null ? 'text-gray-300' : 'text-gray-700'} ${colStart(time, idx)}`}
                        >
                          {value != null ? (row.formatCell ? row.formatCell(value, time) : formatValue(value)) : '—'}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
            {flatRows.length === 0 && (
              <tr>
                <td colSpan={1 + times.length} className="px-4 py-6 text-center text-gray-400">
                  Žádná data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
